import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import { Outlet, useLocation, useParams } from 'react-router-dom';
import { useAppSelector } from '@application/store/useStore';
import useEventRegistrationStyles from './useEventRegistrationStyles';
import { VideoControlProvider, useVideoControlContext } from '../../contexts/VideoControlContext';
import { isVideo } from '@application/helpers/video-detection-helper';
import EventRegistrationHeader from './EventRegistrationHeader';
import { EventRegistrationLanguageProvider } from './EventRegistrationLanguageContext';
import { EventRegistrationFormProvider } from './EventRegistrationFormProvider';
import { EventRegistrationDrawerProvider } from './EventRegistrationDrawerContext';
import EventRegistrationDrawers from './EventRegistrationDrawers';
const EventRegistrationContent = ({ children, embeddedPreview = false, previewLayout }) => {
    const { e } = useParams();
    const location = useLocation();
    const isPreviewPage = location.pathname.includes('/event/preview') || location.pathname.includes('/events/preview');
    const { VDKEventForRegistration } = useAppSelector((state) => state.VDKEvent);
    const { registrationCategories } = useAppSelector((state) => state.faqCategory);
    const hasFaqs = registrationCategories.some((c) => c.questions && c.questions.length > 0);
    const [cachedBackgroundUrl, setCachedBackgroundUrl] = useState(null);
    const [isVideoFile, setIsVideoFile] = useState(false);
    const { videoRef, setHasVideo } = useVideoControlContext();
    const backgroundMedia = VDKEventForRegistration?.uploadedImages?.find((img) => img.isBackgroundImage === true);
    const backgroundMediaUrl = backgroundMedia ? backgroundMedia.url : null;
    const storageKey = `event-background-${e}`;
    useEffect(() => {
        if (e) {
            const storedBackground = localStorage.getItem(storageKey);
            if (storedBackground) {
                const parsedData = JSON.parse(storedBackground);
                setCachedBackgroundUrl(parsedData.url);
                const videoState = parsedData.isVideo;
                setIsVideoFile(videoState);
                setHasVideo(videoState);
            }
        }
    }, [e, storageKey, setHasVideo]);
    useEffect(() => {
        if (backgroundMediaUrl) {
            const isVideoFileDetected = isVideo(undefined, backgroundMediaUrl);
            const fullBackgroundUrl = `${backgroundMediaUrl}`;
            const storedBackground = localStorage.getItem(storageKey);
            const currentStoredUrl = storedBackground ? JSON.parse(storedBackground).url : null;
            if (currentStoredUrl !== fullBackgroundUrl) {
                const backgroundData = {
                    url: fullBackgroundUrl,
                    isVideo: isVideoFileDetected,
                    timestamp: Date.now()
                };
                localStorage.setItem(storageKey, JSON.stringify(backgroundData));
                setCachedBackgroundUrl(fullBackgroundUrl);
                setIsVideoFile(isVideoFileDetected);
                setHasVideo(isVideoFileDetected);
            }
        }
    }, [backgroundMediaUrl, storageKey, setHasVideo]);
    const fullVideoUrl = cachedBackgroundUrl && isVideoFile ? cachedBackgroundUrl : null;
    const backgroundImageUrl = cachedBackgroundUrl && !isVideoFile ? cachedBackgroundUrl : null;
    const styles = useEventRegistrationStyles(backgroundImageUrl, embeddedPreview, previewLayout);
    const showHeader = embeddedPreview || !isPreviewPage;
    return (_jsxs(Box, { sx: {
            ...styles.root,
            height: embeddedPreview ? '100%' : '100dvh',
            maxHeight: embeddedPreview ? '100%' : '100dvh',
            flexDirection: 'column',
            overflow: 'hidden',
            ...(embeddedPreview && {
                position: 'relative',
                isolation: 'isolate'
            })
        }, children: [fullVideoUrl && (_jsx(Box, { component: 'video', ref: videoRef, autoPlay: true, loop: true, muted: true, playsInline: true, sx: styles.backgroundVideo, children: _jsx("source", { src: fullVideoUrl, type: 'video/mp4' }) })), _jsxs(EventRegistrationDrawerProvider, { embeddedPreview: embeddedPreview, previewLayout: previewLayout, children: [showHeader && (_jsx(EventRegistrationHeader, { embeddedPreview: embeddedPreview, previewLayout: previewLayout })), _jsx(Box, { sx: styles.content, children: children ? children : _jsx(Outlet, {}) }), _jsx(EventRegistrationDrawers, {})] })] }));
};
export const EventRegistrationLayout = ({ children, embeddedPreview = false, previewLayout }) => {
    return (_jsx(VideoControlProvider, { children: _jsx(EventRegistrationLanguageProvider, { embeddedPreview: embeddedPreview, children: _jsx(EventRegistrationFormProvider, { children: _jsx(EventRegistrationContent, { embeddedPreview: embeddedPreview, previewLayout: previewLayout, children: children }) }) }) }));
};
